import type { Mode } from './types';

const PREFIX = 'politikdle:v1';

export interface DayState {
  guesses: string[];
  won: boolean;
  hints: number;
}

export interface Stats {
  wins: number;
  streak: number;
  maxStreak: number;
  lastWinDay: number | null;
  /** Sum of guesses over all wins, for the average. */
  totalGuesses: number;
  /** Guess count (10 = 10 or more) → number of wins. */
  dist: Record<string, number>;
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? { ...fallback, ...JSON.parse(raw) } : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode or full storage: the game still works, it just won't remember.
  }
}

export function loadDay(mode: Mode, day: number): DayState {
  return read(`${PREFIX}:day:${mode}:${day}`, { guesses: [], won: false, hints: 0 });
}

export function saveDay(mode: Mode, day: number, state: DayState): void {
  write(`${PREFIX}:day:${mode}:${day}`, state);
}

const emptyStats = (): Stats => ({
  wins: 0,
  streak: 0,
  maxStreak: 0,
  lastWinDay: null,
  totalGuesses: 0,
  dist: {},
});

export function loadStats(mode: Mode): Stats {
  return read(`${PREFIX}:stats:${mode}`, emptyStats());
}

/** Streak as it stands today: it breaks once a whole day passes without a win. */
export function currentStreak(stats: Stats, today: number): number {
  return stats.lastWinDay !== null && today - stats.lastWinDay <= 1 ? stats.streak : 0;
}

export function recordWin(mode: Mode, day: number, guesses: number): Stats {
  const stats = loadStats(mode);
  stats.wins += 1;
  stats.streak = stats.lastWinDay === day - 1 ? stats.streak + 1 : 1;
  stats.maxStreak = Math.max(stats.maxStreak, stats.streak);
  stats.lastWinDay = day;
  stats.totalGuesses += guesses;
  const bucket = String(Math.min(guesses, 10));
  stats.dist[bucket] = (stats.dist[bucket] ?? 0) + 1;
  write(`${PREFIX}:stats:${mode}`, stats);
  return stats;
}

export function loadFlag(name: string): boolean {
  try {
    return localStorage.getItem(`${PREFIX}:flag:${name}`) === '1';
  } catch {
    return false;
  }
}

export function saveFlag(name: string): void {
  try {
    localStorage.setItem(`${PREFIX}:flag:${name}`, '1');
  } catch {
    // ignore
  }
}
